// Deep imports, not the features' barrels: `features/logbook` re-exports the
// API-matching flow, which reaches back into this module, and going through
// both barrels would make the two a module-level cycle for no benefit. The
// same deep-import note `features/flights/link.ts` carries, for the same
// reason.
import { importNormalisedTrack } from '@/features/import/import-track';
import { draftLogbookEntryForTrack } from '@/features/logbook/draft';
import { aircraft as defaultAircraftRepo, type AircraftRepository } from '@/lib/db';
import type { Aircraft, LogbookEntry, Track } from '@/lib/db';
import { SourceError } from '@/lib/sources';
import type { TrackCandidate, TrackIdentityHint, TrackSource } from '@/lib/sources';

/**
 * Retrieving one flight from a network source and putting it in the database.
 *
 * The file path already has this shape — parse, then `importNormalisedTrack`,
 * then draft a logbook entry — and this is the same three steps with the parse
 * replaced by a fetch. Deliberately the same: a track is a track, and a pilot
 * who imported a KML last week and pulls one from adsb.lol today should find the
 * identical duplicate handling, the identical draft, and the identical row in
 * the flight list.
 *
 * Two things it does *not* do, both on purpose.
 *
 * **It never retries.** A fetch from a metered source costs a credit whether it
 * succeeded or not, and a silent retry spends a second one. A failure comes
 * back as a failure with the words to act on.
 *
 * **It never fetches without being told to.** There is no prefetch, no
 * speculative warm-up and no "while we're here". Every request in this module
 * is one a pilot asked for, having seen what it would cost.
 */

export type FetchImportOutcome = 'imported' | 'skipped-duplicate' | 'failed';

export interface FetchImportResult {
  outcome: FetchImportOutcome;
  track?: Track;
  /** The draft the import produced, or the existing entry it matched. */
  entry?: LogbookEntry;
  message?: string;
  detail?: string[];
  /** Present on a failure, so the UI can tell a spent credit from a bad key. */
  errorKind?: SourceError['kind'];
}

export interface FetchImportOptions {
  /**
   * Whether a successful import also drafts a logbook entry. On by default,
   * matching the file path exactly. The logbook's own API-matching flow turns
   * it off: there is already an entry for that flight — the one the pilot
   * typed — and a second one beside it is the duplicate row draft-and-approve
   * exists to avoid.
   */
  draft?: boolean;
  importTrack?: typeof importNormalisedTrack;
  draftEntry?: typeof draftLogbookEntryForTrack;
  aircraftRepo?: AircraftRepository;
  now?: () => string;
}

/**
 * What to fetch: the candidate the search returned, or a bare ref where the
 * caller has nothing else.
 *
 * Prefer the candidate. Some sources — FR24 above all — say which aeroplane a
 * flight belongs to in the search response and not in the track response, so a
 * bare ref throws that identity away and the track is stored anonymous.
 */
export type FetchTarget = string | TrackCandidate;

function identityOf(target: FetchTarget): TrackIdentityHint | undefined {
  if (typeof target === 'string') {
    return undefined;
  }
  const hint: TrackIdentityHint = {
    ...(target.registration === undefined ? {} : { registration: target.registration }),
    ...(target.icaoHex === undefined ? {} : { icaoHex: target.icaoHex }),
  };
  return hint.registration === undefined && hint.icaoHex === undefined ? undefined : hint;
}

/**
 * Write the source's ICAO type designator onto the aircraft record, if that
 * record has none.
 *
 * FR24's summary carries `type`, and this is the only place it can usefully
 * land: `Track` has no type field, and `aircraftTypeLabel` reads the
 * designator off the aircraft, which is what puts a type on the drafted
 * logbook entry. Filled only when the field is blank — a pilot who typed
 * `PA38` against an aeroplane FR24 calls something else meant the one they
 * typed, and the register is not always right about a rebuild or a variant.
 *
 * No record is created. An aeroplane appears in the aircraft list because a
 * pilot put it there; inventing one from a flight that might not even be
 * theirs is a different decision, and not one a fetch should make.
 */
async function fillAircraftType(
  registration: string | undefined,
  icaoType: string | undefined,
  repo: AircraftRepository,
  now: () => string,
): Promise<void> {
  const type = icaoType?.trim().toUpperCase();
  if (registration === undefined || type === undefined || type === '') {
    return;
  }
  const record = await repo.findByRegistration(registration);
  if (record === undefined || (record.icaoType ?? '') !== '') {
    return;
  }
  await repo.put({ ...record, icaoType: type, updatedAt: now() });
}

export async function fetchAndImport(
  source: TrackSource,
  target: FetchTarget,
  options: FetchImportOptions = {},
): Promise<FetchImportResult> {
  const ref = typeof target === 'string' ? target : target.ref;
  const identity = identityOf(target);
  let normalised;
  try {
    normalised = await source.fetch(ref, identity);
  } catch (cause) {
    if (cause instanceof SourceError) {
      return {
        outcome: 'failed',
        message: cause.message,
        errorKind: cause.kind,
        ...(cause.detail === undefined ? {} : { detail: cause.detail }),
      };
    }
    return {
      outcome: 'failed',
      message: `${source.label} returned something this app could not read as a flight.`,
      detail: [cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)],
    };
  }

  /*
   * Before the import, not after: `draftLogbookEntryForTrack` reads the type
   * off the aircraft record to fill the entry's own type field, so a record
   * updated afterwards would leave this flight's draft blank and only help
   * the next one. Best-effort — the track is what the credit bought, and a
   * write to an unrelated record must not turn the fetch into a failure.
   */
  try {
    await fillAircraftType(
      normalised.track.registration,
      typeof target === 'string' ? undefined : target.aircraftType,
      options.aircraftRepo ?? defaultAircraftRepo,
      options.now ?? (() => new Date().toISOString()),
    );
  } catch {
    // Swallowed by design — see above.
  }

  const result = await (options.importTrack ?? importNormalisedTrack)(normalised, {
    requestPersistence: true,
  });

  if (result.outcome === 'failed') {
    return {
      outcome: 'failed',
      message: result.message ?? 'This track could not be saved to the local database.',
      ...(result.detail === undefined ? {} : { detail: result.detail }),
    };
  }

  if (result.outcome === 'skipped-duplicate') {
    return {
      outcome: 'skipped-duplicate',
      ...(result.track === undefined ? {} : { track: result.track }),
      message: result.message ?? 'Already imported. The credit was spent; nothing was duplicated.',
    };
  }

  const track = result.track;
  if (track === undefined) {
    return { outcome: 'imported', message: 'Imported.' };
  }

  /*
   * Best-effort, exactly as the file queue treats it: the track is already
   * safely stored by this point, so a drafting failure must not turn a
   * successful import into a row that reads as failed.
   */
  let entry: LogbookEntry | undefined;
  if (options.draft !== false) {
    try {
      const drafted = await (options.draftEntry ?? draftLogbookEntryForTrack)(
        track,
        normalised.points,
      );
      entry = drafted.entry;
    } catch {
      // Swallowed by design — see the comment above.
    }
  }

  return {
    outcome: 'imported',
    track,
    ...(entry === undefined ? {} : { entry }),
    ...(result.message === undefined ? {} : { message: result.message }),
  };
}

/**
 * A track already stored that covers this candidate's aircraft and time.
 *
 * The cheap half of duplicate detection, run *before* anything is fetched:
 * `importNormalisedTrack` catches a duplicate for certain, but only once the
 * track has been downloaded and the credit spent. Same aircraft, overlapping
 * time is enough to say "you already have this" beforehand, which is what both
 * the search screen and the startup check need — one to warn, the other to
 * skip the fetch entirely.
 *
 * @param aircraft  the aeroplane the search was *for*, when there is one. An
 *   ADS-B candidate carries a hex code and no registration, and a track
 *   imported from a KML carries a registration and no hex; without this the
 *   two never match each other and every flight looks new.
 */
export function alreadyHeld(
  candidate: TrackCandidate,
  stored: readonly Track[],
  aircraft?: Pick<Aircraft, 'registration' | 'icaoHex'>,
): Track | undefined {
  if (candidate.startTime === undefined) {
    return undefined;
  }
  const start = Date.parse(candidate.startTime);
  const end = Date.parse(candidate.endTime ?? candidate.startTime);
  const registration = candidate.registration ?? aircraft?.registration;
  const icaoHex = candidate.icaoHex ?? aircraft?.icaoHex;

  return stored.find((track) => {
    const matchesAircraft =
      (registration !== undefined && track.registration === registration) ||
      (icaoHex !== undefined && track.icaoHex === icaoHex);
    if (!matchesAircraft) {
      return false;
    }
    return Date.parse(track.startTime) <= end && Date.parse(track.endTime) >= start;
  });
}

/** One line describing a candidate, for a confirmation prompt or a result row. */
export function describeCandidate(candidate: TrackCandidate): string {
  const route =
    candidate.departure === undefined && candidate.arrival === undefined
      ? undefined
      : `${candidate.departure ?? '?'} → ${candidate.arrival ?? '?'}`;
  return [candidate.registration ?? candidate.callsign ?? candidate.icaoHex, route]
    .filter((part) => part !== undefined)
    .join(' · ');
}
